/**
 * SalesMeetingDebriefView - Renders sales meeting debrief
 */

import React, { useState } from 'react';
import { Text, Flex, Icon } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import type { SalesMeetingDebrief, ContextInsight, WatchOut } from 'App/resources/Model/MeetingDebrief';
import {
	parseDebrief,
	SummaryCard,
	ActionItemList,
	DiscussedSection,
	PersonItemList,
	StatusBadge
} from './MeetingDebriefCommon';
import './SalesMeetingDebriefView.scss';

// ============================================================================
// Deal Reality Field Component (specific to Sales view)
// ============================================================================

function DealRealityField({ label, value }: { label: string; value: string }) {
	const parseValue = (
		text: string
	): { value: string; subDescription?: string; color?: 'danger-600' | 'success-600' } => {
		const lowerText = text.toLowerCase();

		if (lowerText.includes('unconfirmed') || lowerText.includes('not confirmed')) {
			const parts = text
				.split(/[.,;]/)
				.map(p => p.trim())
				.filter(p => p);
			const value = parts[0] || text;
			const subDescription = parts.slice(1).join('. ');
			return { value, subDescription, color: 'danger-600' };
		}

		if (lowerText.includes('confirmed') && (lowerText.includes('is') || lowerText.match(/\w+\s+confirmed/))) {
			const match = text.match(/^([^.,;]+(?:confirmed|is\s+confirmed))[.,;]?\s*(.*)$/i);
			if (match) {
				return { value: match[1].trim(), subDescription: match[2].trim() || undefined, color: 'success-600' };
			}
		}

		const sentences = text
			.split(/[.,;]/)
			.map(s => s.trim())
			.filter(s => s);
		if (sentences.length > 1) {
			return { value: sentences[0], subDescription: sentences.slice(1).join('. ') };
		}

		return { value: text };
	};

	const parsed = parseValue(value);

	return (
		<Flex direction="column" gap="u1">
			<Text size="sm" color="neutral-600">
				{label}
			</Text>
			<Text bold color={parsed.color || 'black'}>
				{parsed.value}
			</Text>
			{parsed.subDescription && (
				<Text size="sm" color="neutral-600">
					{parsed.subDescription}
				</Text>
			)}
		</Flex>
	);
}

// ============================================================================
// Sales Meeting Debrief View
// ============================================================================

interface SalesMeetingDebriefViewProps {
	debrief: SalesMeetingDebrief | string | null;
	t: (text: string) => string;
	hideTitle?: boolean;
}

export function SalesMeetingDebriefView({ debrief: rawDebrief, t, hideTitle }: SalesMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');
	const [isDealRealityExpanded, setIsDealRealityExpanded] = useState(true);
	const [isThreeWhysExpanded, setIsThreeWhysExpanded] = useState(true);
	const [isPeopleExpanded, setIsPeopleExpanded] = useState(true);
	const [isDiscussedExpanded, setIsDiscussedExpanded] = useState(true);
	const [isWatchOutExpanded, setIsWatchOutExpanded] = useState(true);
	const [isFromResearchExpanded, setIsFromResearchExpanded] = useState(true);
	const [isNextStepsExpanded, setIsNextStepsExpanded] = useState(true);
	const [isQuestionsExpanded, setIsQuestionsExpanded] = useState(true);

	const debrief = parseDebrief<SalesMeetingDebrief>(rawDebrief);

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
			<SummaryCard summary={debrief.summary} classes={classes} t={t} hideTitle={hideTitle}>
				{debrief.deal_reality ? (
					<>
						<Flex
							justifyContent="space-between"
							alignItems="center"
							className={classes.elem('dealRealityHeader').b()}
							onClick={() => setIsDealRealityExpanded(!isDealRealityExpanded)}
							spacing="mb4"
						>
							<Text size="lg" bold color="black">
								{t('meetingDebrief.dealReality')}
							</Text>
							<Icon
								name="chevron-down"
								className={classes
									.elem('dealRealityCaret')
									.mod(isDealRealityExpanded ? 'expanded' : 'collapsed')
									.b()}
							/>
						</Flex>
						<div
							className={classes
								.elem('dealRealityContent')
								.mod(isDealRealityExpanded ? 'expanded' : 'collapsed')
								.b()}
						>
							<div className={classes.elem('dealRealitySection').b()}>
								<Text bold color="black" spacing="mb4">
									{debrief.deal_reality.overall}
								</Text>
								<Flex direction="column" gap="u4" spacing="pt4">
									<DealRealityField
										label={t('meetingDebrief.budget')}
										value={debrief.deal_reality.budget}
									/>
									<DealRealityField
										label={t('meetingDebrief.timeline')}
										value={debrief.deal_reality.timeline}
									/>
									<DealRealityField
										label={t('meetingDebrief.decisionMaker')}
										value={debrief.deal_reality.decision_maker}
									/>
									<DealRealityField
										label={t('meetingDebrief.competition')}
										value={debrief.deal_reality.competition}
									/>
									{debrief.what_happened?.momentum ? (
										<DealRealityField
											label={t('meetingDebrief.momentum')}
											value={debrief.what_happened.momentum}
										/>
									) : null}
								</Flex>
							</div>
						</div>
					</>
				) : null}
			</SummaryCard>

			{debrief.what_happened?.people && debrief.what_happened.people.length > 0 ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('peopleHeader').b()}
						onClick={() => setIsPeopleExpanded(!isPeopleExpanded)}
					>
						<Text size="lg" bold color="black">
							{t('meetingDebrief.people')}
						</Text>
						<Icon
							name="chevron-down"
							className={classes
								.elem('peopleCaret')
								.mod(isPeopleExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('peopleContent')
							.mod(isPeopleExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('peopleSection').b()}>
							<PersonItemList people={debrief.what_happened.people} classes={classes} />
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.three_whys ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('threeWhysHeader').b()}
						onClick={() => setIsThreeWhysExpanded(!isThreeWhysExpanded)}
					>
						<Text size="lg" bold color="black">
							{t('meetingDebrief.threeWhys')}
						</Text>
						<Icon
							name="chevron-down"
							className={classes
								.elem('threeWhysCaret')
								.mod(isThreeWhysExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('threeWhysContent')
							.mod(isThreeWhysExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('threeWhysContainer').b()}>
							<Flex direction="column" gap="u4" className={classes.elem('threeWhysColumn').b()}>
								<Flex justifyContent="space-between" alignItems="flex-start" spacing="mb2">
									<Text size="sm" bold color="black">
										{t('meetingDebrief.whyChange')}
									</Text>
									<StatusBadge
										variant="solid"
										value={debrief.three_whys.why_change?.level || 'partial'}
										t={t}
									/>
								</Flex>
								<Text size="sm" color="grey-11">
									{debrief.three_whys.why_change?.status}
								</Text>
							</Flex>
							<div className={classes.elem('threeWhysDivider').b()} />
							<Flex direction="column" gap="u4" className={classes.elem('threeWhysColumn').b()}>
								<Flex justifyContent="space-between" alignItems="flex-start" spacing="mb2">
									<Text size="sm" bold color="black">
										{t('meetingDebrief.whyNow')}
									</Text>
									<StatusBadge
										variant="solid"
										value={debrief.three_whys.why_now?.level || 'partial'}
										t={t}
									/>
								</Flex>
								<Text size="sm" color="grey-11">
									{debrief.three_whys.why_now?.status}
								</Text>
							</Flex>
							<div className={classes.elem('threeWhysDivider').b()} />
							<Flex direction="column" gap="u4" className={classes.elem('threeWhysColumn').b()}>
								<Flex justifyContent="space-between" alignItems="flex-start" spacing="mb2">
									<Text size="sm" bold color="black">
										{t('meetingDebrief.whyUs')}
									</Text>
									<StatusBadge
										variant="solid"
										value={debrief.three_whys.why_us?.level || 'partial'}
										t={t}
									/>
								</Flex>
								<Text size="sm" color="grey-11">
									{debrief.three_whys.why_us?.status}
								</Text>
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.what_happened?.topics && debrief.what_happened.topics.length > 0 ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('discussedHeader').b()}
						onClick={() => setIsDiscussedExpanded(!isDiscussedExpanded)}
					>
						<Flex gap="u2" alignItems="center">
							<Text size="lg" bold color="black">
								{t('meetingDebrief.whatWeDiscussed')}
							</Text>
							<Text size="sm" color="grey-11">
								{'• '}
								{new Date(debrief.date).toLocaleDateString('en-GB', {
									day: 'numeric',
									month: 'short',
									year: 'numeric'
								})}
							</Text>
						</Flex>
						<Icon
							name="chevron-down"
							className={classes
								.elem('discussedCaret')
								.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('discussedContent')
							.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('discussedSection').b()}>
							<Flex direction="column" spacing="p4">
								<DiscussedSection topics={debrief.what_happened.topics} classes={classes} />
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.context_insights && debrief.context_insights.length > 0 ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('fromResearchHeader').b()}
						onClick={() => setIsFromResearchExpanded(!isFromResearchExpanded)}
					>
						<Text size="lg" bold color="black">
							{t('meetingDebrief.fromYourResearch')}
						</Text>
						<Icon
							name="chevron-down"
							className={classes
								.elem('fromResearchCaret')
								.mod(isFromResearchExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('fromResearchContent')
							.mod(isFromResearchExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('fromResearchSection').b()}>
							<Flex direction="column" spacing="p4">
								{debrief.context_insights.map((insight: ContextInsight, idx: number) => (
									<Flex
										key={`insight-${idx}`}
										direction="column"
										gap="u1"
										spacing={idx < debrief.context_insights!.length - 1 ? 'mb4' : undefined}
									>
										<Text bold color="info-500">
											{insight.insight}
										</Text>
										<Text size="sm" color="grey-13">
											{insight.implication}
										</Text>
									</Flex>
								))}
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.watch_out && debrief.watch_out.length > 0 ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('watchOutHeader').b()}
						onClick={() => setIsWatchOutExpanded(!isWatchOutExpanded)}
					>
						<Text size="lg" bold color="black">
							{t('meetingDebrief.watchOut')}
						</Text>
						<Icon
							name="chevron-down"
							className={classes
								.elem('watchOutCaret')
								.mod(isWatchOutExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('watchOutContent')
							.mod(isWatchOutExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('watchOutSection').b()}>
							<Flex direction="column" spacing="p4">
								{debrief.watch_out.map((item: WatchOut, idx: number) => (
									<Flex
										key={`watch-${idx}`}
										direction="column"
										gap="u1"
										spacing={idx < debrief.watch_out!.length - 1 ? 'mb4' : undefined}
									>
										<Text bold color="danger-600">
											{item.issue}
										</Text>
										<Text size="sm" color="grey-13">
											{item.action}
										</Text>
									</Flex>
								))}
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.next_steps ? (
				<>
					<Flex direction="column" gap="u4">
						<Flex
							justifyContent="space-between"
							alignItems="center"
							className={classes.elem('nextStepsHeader').b()}
							onClick={() => setIsNextStepsExpanded(!isNextStepsExpanded)}
						>
							<Text size="lg" bold color="black">
								{t('meetingDebrief.nextSteps')}
							</Text>
							<Icon
								name="chevron-down"
								className={classes
									.elem('nextStepsCaret')
									.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
									.b()}
							/>
						</Flex>
						<div
							className={classes
								.elem('nextStepsContent')
								.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
								.b()}
						>
							<div className={classes.elem('nextStepsSection').b()}>
								{debrief.next_steps.actions && (
									<ActionItemList actions={debrief.next_steps.actions} classes={classes} />
								)}
							</div>
						</div>
					</Flex>

					{debrief.next_steps.questions_for_next_time &&
					debrief.next_steps.questions_for_next_time.length > 0 ? (
						<Flex direction="column" gap="u4">
							<Flex
								justifyContent="space-between"
								alignItems="center"
								className={classes.elem('questionsHeader').b()}
								onClick={() => setIsQuestionsExpanded(!isQuestionsExpanded)}
							>
								<Text size="lg" bold color="black">
									{t('meetingDebrief.questionsForNextTime')}
								</Text>
								<Icon
									name="chevron-down"
									className={classes
										.elem('questionsCaret')
										.mod(isQuestionsExpanded ? 'expanded' : 'collapsed')
										.b()}
								/>
							</Flex>
							<div
								className={classes
									.elem('questionsContent')
									.mod(isQuestionsExpanded ? 'expanded' : 'collapsed')
									.b()}
							>
								<div className={classes.elem('questionsSection').b()}>
									<Flex direction="column" spacing="p4">
										{debrief.next_steps.questions_for_next_time.map(
											(question: string, idx: number) => (
												<Flex
													key={`question-${idx}`}
													gap="u2"
													alignItems="center"
													spacing={
														idx < debrief.next_steps.questions_for_next_time!.length - 1
															? 'mb3'
															: undefined
													}
												>
													<Text size="sm" color="grey-11">
														{'• '}
														{question}
													</Text>
												</Flex>
											)
										)}
									</Flex>
								</div>
							</div>
						</Flex>
					) : null}
				</>
			) : null}
		</Flex>
	);
}
